import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import type { ReplayInfo } from "../../../lib/api/socialApi";
import { isSupported } from "../../../lib/boot/device";
import { useBootDeps } from "../../../lib/boot/deps";
import { Button } from "../../../components/ui/Button";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { formatClock } from "../../course";
import { useI18n } from "../../../i18n";
import { SPEEDS } from "../../replay";
import type { Speed } from "../../replay";
import { RoomReplayView } from "../../replay";
import type { ObserverSource } from "../../replay";
import { useSession } from "../../../store/session/SessionProvider";
import { observerLabels, useShareObserverApi } from "../../share";
import { shareDate, shareLength } from "../../share";
import type { ShareReplay, ShareView } from "../../share";
import "../../../styles/replay.css";
import "../../../styles/share.css";
import type { ShareLoad, PlayTarget } from "../types";

/**
 * FS-01-05 관찰자 진입(`/share/:token` · SCR v0.6 sfs-01-05 · v0.7 검토 §3.5 5-1~5-9 · v07-21~26) — 교사가 만든 자녀 링크로 들어온 보호자.
 *  - 플래그 OFF: 중립 안내 '아직 열리지 않은 기능이에요'(FS-C1-03 변형 · FE 코드 없음 · 재입장 유도 없음 — 서버가 토큰을 보지 않는다)
 *  - 세션 없음: 'Campus 로 들어가서 보기'(campusUrl — Campus 가 Mentor 로 Field 를 다시 런치 · target_link_uri = 이 링크)
 *  - 세션 있음: 링크 유효 카드(반 label · 활동 · 자녀 별명 · 만료) → ▶ 재생(FS-14-01 관찰자 변형)
 *  - 만료 · 취소 · 모르는 링크 → FS-C1-03 '여기는 볼 수 없어요'(코드 없음) · 기관 밖 · 자녀 아님 · 관찰자 아님 → FS-C1-03 + FE-302
 *  - 영상이 없고(리플레이 링크) 기기 미지원 → FS-C1-04(FE-601) · 영상(MP4)은 기기와 무관하게 재생
 *  - 열람 기록 안내 한 줄(5-8) · 다른 아이 별명은 서버 정책(otherNames)대로 — friend 면 '친구'(5-6)
 * 실패 · 비교 · 다운로드 · 공유 다시 만들기는 없다(DR-01 · 5-9).
 */
export function ShareScreen() {
  const { t } = useI18n();
  const { token } = useParams();
  const state = useSession();
  const api = useShareObserverApi();
  const [load, setLoad] = useState<ShareLoad>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const [play, setPlay] = useState<PlayTarget>(null);
  const settled = state.status !== "idle" && state.status !== "pending";
  const hasSession = state.status === "ready";

  useTopBar({
    icon: "👪",
    title: t("share.appTitle"),
    chips: [{ label: t("share.chipObserver"), testId: "share-chip-observer" }],
    frame: "adult",
  });

  useEffect(() => {
    if (!token || !settled) return;
    let alive = true;
    setLoad({ status: "loading" });
    api
      .open(token)
      .then((open) => alive && setLoad({ status: "done", open }))
      .catch((e: unknown) => alive && setLoad({ status: "error", code: toApiError(e).code }));
    return () => {
      alive = false;
    };
    // 세션이 생기거나 없어질 때만 다시 연다(토큰 갱신으로는 다시 열지 않는다 — 열람 수가 두 번 늘지 않게)
  }, [api, token, settled, seq, hasSession]);

  const body = (() => {
    if (!token)
      return (
        <Notice testId="share-no-token" screen="FS-01-05" tone="info" title={t("share.noToken")}>
          <p className="pf-muted">{t("share.noTokenNext")}</p>
        </Notice>
      );
    if (load.status === "loading")
      return (
        <div className="pf-pane pf-share__card" aria-busy="true" data-testid="share-loading">
          <p className="pf-muted">{t("share.checking")}</p>
        </div>
      );
    if (load.status === "error")
      return (
        <Notice
          testId="share-error"
          screen="FS-C1"
          tone="warn"
          title={t("share.loadFailed")}
          code={load.code}
        >
          <p className="pf-muted">{t("share.loadFailedNext")}</p>
          <Button variant="ghost" icon="↻" onClick={() => setSeq((n) => n + 1)}>
            {t("common.retry")}
          </Button>
        </Notice>
      );
    const o = load.open;
    switch (o.kind) {
      case "off":
        // 5-1 — 중립 안내만(FE 코드 · 재입장 · Campus 버튼 없음)
        return <Notice testId="share-off" screen="FS-C1-03" tone="info" title={t("share.off")} />;
      case "campus":
        return <CampusStep url={o.campusUrl} />;
      case "gone":
        return (
          <Notice
            testId="share-gone"
            screen="FS-C1-03"
            tone="info"
            title={t("share.goneTitle")}
            state={o.state}
          >
            <p>
              <b>{t(`share.gone.${o.state}`)}</b> {t("share.goneNext")}
            </p>
          </Notice>
        );
      case "denied":
        return (
          <Notice
            testId="share-denied"
            screen="FS-C1-03"
            tone="err"
            title={t("share.goneTitle")}
            code="FE-302"
            state={o.reason}
          >
            <p>
              <b>{t(`share.denied.${o.reason}`)}</b> {t("share.deniedNext")}
            </p>
          </Notice>
        );
      case "ok":
        return play ? (
          <Playback token={token} view={o.view} play={play} onBack={() => setPlay(null)} />
        ) : (
          <ValidCard view={o.view} onPlay={setPlay} />
        );
    }
  })();

  const playing = load.status === "done" && load.open.kind === "ok" && play !== null;
  return (
    <ScreenRoot
      id="FS-01-05"
      title={t("share.screenTitle")}
      hideTitle
      fill={playing}
      className={`pf-share${playing ? " pf-share--playing" : ""}`}
    >
      {body}
    </ScreenRoot>
  );
}

/** 상태 카드(FS-C1-03 · FS-C1-04 변형 — 보호자 문구 · 코드는 있을 때만 '문의 코드') */
function Notice({
  testId,
  screen,
  tone,
  title,
  code,
  state,
  children,
}: {
  testId: string;
  screen: string;
  tone: "info" | "warn" | "err";
  title: string;
  code?: string;
  state?: string;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div
      className="pf-pane pf-pane--hero pf-share__card"
      data-testid={testId}
      data-screen={screen}
      data-state={state}
    >
      <div
        className={`pf-banner pf-banner--${tone}`}
        role={tone === "err" ? "alert" : "status"}
        data-fe-code={code}
      >
        <div className="pf-banner__text">
          <b className="pf-banner__cause">{title}</b>
          {code && <span className="pf-banner__code"> ({t("common.codeLabel", { code })})</span>}
        </div>
      </div>
      {children}
    </div>
  );
}

/** 5-2 ① 링크만 연 경우 — Campus 보호자 계정으로 들어가면 Campus 가 Mentor 로 Field 를 런치해 이 링크로 돌려보낸다 */
function CampusStep({ url }: { url: string | null }) {
  const { t } = useI18n();
  return (
    <div className="pf-pane pf-pane--hero pf-share__card" data-testid="share-campus">
      <h2 className="pf-h3">{t("share.campusTitle")}</h2>
      <p className="pf-muted">{t(url ? "share.campusText" : "share.campusNoUrl")}</p>
      {url && (
        <Button
          variant="accent"
          icon="↪"
          block
          data-testid="share-campus-go"
          onClick={() => window.location.assign(url)}
        >
          {t("share.campusGo")}
        </Button>
      )}
      <p className="pf-muted pf-share__note">{t("share.campusNote")}</p>
    </div>
  );
}

/** 링크 유효 카드(와이어 sfs-01-05 pane hero) */
function ValidCard({ view, onPlay }: { view: ShareView; onPlay: (p: PlayTarget) => void }) {
  const { t } = useI18n();
  const deps = useBootDeps();
  const date = shareDate(view.expiresAt);
  const supported = useMemo(() => isSupported(deps.detectDevice()), [deps]);
  // 5-5 — MP4 가 있으면 기본 · 리플레이는 선택(기기가 되면)
  const primary: PlayTarget = view.video ? "video" : view.replay ? "replay" : null;
  const blocked = primary === "replay" && !supported;
  if (blocked)
    return (
      <div
        className="pf-pane pf-pane--hero pf-share__card"
        data-testid="fs-c1-04"
        data-screen="FS-C1-04"
      >
        <div className="pf-banner pf-banner--warn" role="status" data-fe-code="FE-601">
          <div className="pf-banner__text">
            <b className="pf-banner__cause">{t("share.device")}</b>{" "}
            <span className="pf-banner__next">{t("share.deviceNext")}</span>
            <span className="pf-banner__code"> ({t("common.codeLabel", { code: "FE-601" })})</span>
          </div>
          <ul className="pf-chips" aria-label={t("device.supported")}>
            {t("device.supported")
              .split(" · ")
              .map((b) => (
                <li key={b} className="pf-chip-static">
                  {b}
                </li>
              ))}
          </ul>
        </div>
      </div>
    );
  const len = view.video?.durationS != null ? shareLength(t, view.video.durationS) : null;
  return (
    <div className="pf-pane pf-pane--hero pf-share__card" data-testid="share-valid">
      <h2 className="pf-h2">{t("share.cardTitle")}</h2>
      <div className="pf-banner pf-banner--ok" role="status" data-testid="share-valid-banner">
        <div className="pf-banner__text">
          <span aria-hidden="true">✔ </span>
          {t("share.valid", {
            course: view.course,
            activity: view.activity,
            child: view.child,
            date,
          })}
        </div>
      </div>
      <div className="pf-share__work">
        <div className="pf-share__thumb" aria-hidden="true">
          {view.video ? "🎬" : "🔁"}
        </div>
        <div>
          <b data-testid="share-work">{t(view.video ? "share.workVideo" : "share.workReplay")}</b>
          <div className="pf-muted" data-testid="share-meta">
            {len ? t("share.meta", { len }) : t("share.metaNoLen")}
          </div>
        </div>
      </div>
      <Button
        variant="accent"
        icon="▶"
        block
        data-testid="share-play"
        disabled={!primary}
        onClick={() => onPlay(primary)}
      >
        {t("share.play")}
      </Button>
      {view.video && view.replay && supported && (
        <Button
          variant="ghost"
          icon="🔁"
          data-testid="share-play-replay"
          onClick={() => onPlay("replay")}
        >
          {t("share.playReplay")}
        </Button>
      )}
      <div className="pf-pane pf-share__dim">
        <p className="pf-muted" data-testid="share-names">
          {t(view.otherNames === "all" ? "share.namesAll" : "share.namesFriend")}
        </p>
        <p className="pf-muted" data-testid="share-record">
          {t("share.record")}
        </p>
      </div>
    </div>
  );
}

/** FS-14-01 관찰자 변형 — MP4(기본) 또는 룸 리플레이(선택) */
function Playback({
  token,
  view,
  play,
  onBack,
}: {
  token: string;
  view: ShareView;
  play: PlayTarget;
  onBack: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="pf-share__play" data-testid="share-playback" data-kind={play}>
      <div className="pf-share__playhead">
        <Button variant="ghost" icon="←" className="pf-btn--sm" onClick={onBack}>
          {t("share.back")}
        </Button>
        <b>{t("share.playTitle", { course: view.course, child: view.child })}</b>
        <span className="pf-muted pf-row__end" data-testid="share-play-note">
          {t(view.otherNames === "all" ? "share.namesAll" : "share.namesFriend")}
        </span>
      </div>
      {play === "video" && view.video ? (
        <ObserverVideo url={view.video.url} durationS={view.video.durationS} />
      ) : (
        <ObserverReplay token={token} view={view} />
      )}
    </div>
  );
}

/**
 * MP4 재생(5-5 기본) — 재생 · 일시정지 · 배속만(5-9). 기본 컨트롤 · 다운로드 · PIP · 우클릭 메뉴를 끈다.
 * 영상 안 라벨은 렌더 때 굳었다(별명만 — 영상 링크는 otherNames "all" 일 때만 만들어진다).
 */
export function ObserverVideo({ url, durationS }: { url: string; durationS: number | null }) {
  const { t } = useI18n();
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [now, setNow] = useState(0);
  const [dur, setDur] = useState<number | null>(durationS);
  useEffect(() => {
    if (ref.current) ref.current.playbackRate = speed;
  }, [speed]);
  const doPlay = () => {
    const v = ref.current;
    if (!v) return;
    try {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => setPlaying(false));
    } catch {
      /* 재생을 못 하는 환경 — 상태만 바꾼다 */
    }
    setPlaying(true);
  };
  const doPause = () => {
    try {
      ref.current?.pause();
    } catch {
      /* 무시 */
    }
    setPlaying(false);
  };
  const total = dur ?? 0;
  const frac = total > 0 ? Math.min(1, now / total) : 0;
  const clock = t("replay.time", {
    now: formatClock(now, false),
    total: formatClock(total, false),
  });
  return (
    <div className="pf-replay__left pf-share__videobox">
      <div className="pf-share__screen">
        <video
          ref={ref}
          className="pf-share__video"
          src={url}
          playsInline
          preload="metadata"
          controls={false}
          controlsList="nodownload noremoteplayback noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d) && d > 0) setDur(d);
          }}
          onEnded={() => setPlaying(false)}
          aria-label={t("share.videoLabel")}
          data-testid="share-video"
        />
        <div className="pf-hud pf-hud--timer" data-testid="replay-time">
          {clock}
        </div>
      </div>
      <div className="pf-pane pf-replay__controls" data-testid="replay-controls">
        <div className="pf-replay__row">
          <Button
            variant="primary"
            className="pf-replay__btn"
            aria-label={t("replay.play")}
            aria-pressed={playing}
            disabled={playing}
            onClick={doPlay}
            data-testid="replay-play"
          >
            ▶
          </Button>
          <Button
            variant="ghost"
            className="pf-replay__btn"
            aria-label={t("replay.pause")}
            disabled={!playing}
            onClick={doPause}
            data-testid="replay-pause"
          >
            ⏸
          </Button>
          <div
            className="pf-replay__timeline"
            role="progressbar"
            aria-label={t("replay.timeline")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(frac * 100)}
            aria-valuetext={clock}
            data-testid="replay-timeline"
          >
            <span className="pf-replay__bar">
              <span className="pf-replay__fill" style={{ width: `${frac * 100}%` }} />
            </span>
          </div>
          <div className="pf-replay__speeds" role="radiogroup" aria-label={t("replay.speed")}>
            {SPEEDS.map((s) => (
              <Button
                key={s}
                role="radio"
                aria-checked={speed === s}
                variant={speed === s ? "accent" : "ghost"}
                className="pf-replay__btn"
                onClick={() => setSpeed(s)}
                data-speed={s}
              >
                {`${s}×`}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** 룸 리플레이(5-5 선택) — P3-S3 재생기를 관찰자 경로(토큰)로 */
function ObserverReplay({ token, view }: { token: string; view: ShareView }) {
  const { t } = useI18n();
  const api = useShareObserverApi();
  const deps = useBootDeps();
  const [rep, setRep] = useState<ShareReplay | null>(null);
  const [err, setErr] = useState<ErrorCode | null>(null);
  const supported = useMemo(() => isSupported(deps.detectDevice()), [deps]);
  useEffect(() => {
    if (!supported) return;
    let alive = true;
    api
      .replay(token)
      .then((r) => alive && setRep(r))
      .catch((e: unknown) => alive && setErr(toApiError(e).code));
    return () => {
      alive = false;
    };
  }, [api, token, supported]);
  const source: ObserverSource | null = useMemo(
    () =>
      rep && {
        file: () => api.replayFile(token),
        bundle: (id) => api.bundle(token, id),
        labels: observerLabels(rep.machines, rep.otherNames ?? view.otherNames, t("share.friend")),
      },
    [rep, api, token, view.otherNames, t],
  );
  const info: ReplayInfo | null = useMemo(
    () =>
      rep && {
        replayId: rep.replayId,
        nickname: "",
        completed: true,
        presetKey: "",
        modeId: "",
        // 명부 묶음 — snapshotIds + 기계별 snapshotId(서버가 meta 에서 pid 만 뺐어도 묶음은 다 받는다)
        snapshotIds: [
          ...new Set([
            ...rep.snapshotIds,
            ...rep.machines.flatMap((m) => (m.snapshotId ? [m.snapshotId] : [])),
          ]),
        ],
        meta: (rep.meta ?? { format: rep.format }) as unknown as ReplayInfo["meta"],
        hashOk: true,
        expiresAt: view.expiresAt ?? "",
        mine: false,
      },
    [rep, view.expiresAt],
  );
  if (!supported)
    return (
      <div
        className="pf-banner pf-banner--warn"
        role="status"
        data-testid="fs-c1-04"
        data-fe-code="FE-601"
      >
        <div className="pf-banner__text">
          <b className="pf-banner__cause">{t("share.device")}</b>{" "}
          <span className="pf-banner__next">{t("share.deviceNext")}</span>
        </div>
      </div>
    );
  if (err)
    return (
      <div
        className="pf-banner pf-banner--warn"
        role="status"
        data-testid="share-replay-error"
        data-fe-code={err}
      >
        <div className="pf-banner__text">
          <b className="pf-banner__cause">{t("share.loadFailed")}</b>{" "}
          <span className="pf-banner__next">{t("share.loadFailedNext")}</span>
          <span className="pf-banner__code"> ({t("common.codeLabel", { code: err })})</span>
        </div>
      </div>
    );
  if (!source || !info)
    return (
      <div className="pf-pane" aria-busy="true">
        <p className="pf-muted">{t("share.checking")}</p>
      </div>
    );
  return <RoomReplayView info={info} teacher={false} observer={source} />;
}
